import { apiFetch } from '../lib/apiFetch';
import { formatBusinessTime } from '../lib/businessTime';

export interface OrderLine {
  id: string;
  itemType: string;
  productName: string;
  quantity: number;
  priceEach: number;
  totalPrice: number;
}

/** A date-bounded phase WITHIN a single order. Each section groups one or more
 *  of the order's lines under its own rental window (constrained to sit inside
 *  the order's window), is tier-priced on its own dates, and can be started/
 *  stopped independently. Replaces the old "one order per phase" model.
 *
 *  Shape matches the backend serializer (repositories/order.py) verbatim — the
 *  order detail page and sectionService consume this raw, not via mapDbOrder. */
export interface OrderSection {
  id: string;
  sequence: number;
  label?: string | null;
  /** Section delivery/drop date (ISO). Must be >= the order's starts_at. */
  starts_at: string | null;
  /** Section pickup date (ISO). Must be <= the order's stops_at. */
  stops_at: string | null;
  /** Set once the section is activated (its lines billed by finalizing the
   *  order's invoice). Null = not yet invoiced. */
  activated_at?: string | null;
}

/** Raw order-detail payload (GET /orders/<id> and the section endpoints), as
 *  emitted by repositories/order.py to_dict — snake_case, NOT mapped through
 *  mapDbOrder. Carries the section timeline + line→section tags the order page
 *  joins against. Intentionally loose on the rest (the page reads many raw
 *  fields directly). */
export interface RawOrderWithSections {
  id: string;
  sections?: OrderSection[];
  lines?: Array<{ id: string; booqable_line_id: string | null; section_id: string | null;[k: string]: any }>;
  [k: string]: any;
}

export interface Order {
  id: string;
  /** Booqable order id, populated from the Supabase mirror. Used for "Open in Booqable" links. */
  booqable_id?: string | null;
  orderNumber: string;
  customerName: string;
  customerId: string | null;
  customerEmail: string;
  startDate: string;
  endDate: string;
  items: string;
  orderLines: OrderLine[];
  itemCount: number;
  location: string;
  order_coordinates?: string;
  status: 'late' | 'upcoming';
  booqableStatus: 'new' | 'draft' | 'reserved' | 'started' | 'stopped' | 'archived' | 'canceled';
  price: number;
  paidAmount: number;
  // Derived in mapDbOrder from the mirror's money columns (paid vs tax-inclusive
  // total), NOT read from the persisted payment_status enum — see the derivation
  // there for why. null for a zero-dollar / not-yet-totaled order (no balance to
  // owe → PaymentStatusPill renders a dash, not a misleading "Paid").
  paymentStatus: 'paid' | 'partially_paid' | 'payment_due' | 'overpaid' | 'process_deposit' | null;
  createdAt: string;
  /** Last *manual* edit by a user (staff or customer) — drives the "Last
   *  modified" column. Empty until the order's first in-app edit. NOT the
   *  sync-bumped updated_at. */
  lastModified: string;
  /** Booqable-native tags mirrored to orders.tag_list. Drives the Orders
   *  list tag filter (#20). System tags like SANDBOX are present here but
   *  suppressed in the filter UI. */
  tagList: string[];
  /** Website booking-flow placement fields (display-only, #8). */
  debrisDescription?: string | null;
  prohibitedItemsAcknowledged?: boolean | null;
  /** Staff waiver for the card technology fee on this order's pay-link charges. */
  waiveTechFee?: boolean;
  /** Origin channel: where the order came from. Null on legacy rows predating
   *  provenance tracking (all backfilled to 'booqable_sync'). */
  source?: 'website' | 'admin' | 'booqable_sync' | null;
  /** Creating staff member's name — populated for admin-sourced orders only;
   *  null for website/sync (no staff actor). */
  createdByName?: string | null;
  /** Parent project envelope when this order is one phase of a multi-phase
   *  rolloff project; null for one-off orders. Drives the Orders list
   *  "Project" column (links to the project). */
  project?: { id: string; name: string | null; projectNumber: number | null } | null;
  /** Effective pay-link methods for this order, resolved from the owning
   *  customer's allowed_payment_methods (read-only). Always non-empty: an
   *  unset/empty customer setting resolves to ['card'] — the card-only default
   *  the pay link uses. Drives the Orders list "Pay methods" column so staff
   *  can spot ACH-enabled / ACH-only customers while scanning. */
  paymentMethods: Array<'card' | 'ach'>;
  /** True once an email carrying an invoice PDF has been sent for this order
   *  (tracked in email_sends). Drives the Orders list "Invoice sent" column —
   *  a green checkmark when true, blank otherwise. */
  invoiceSent: boolean;
}

/** Resolve the effective pay-link methods from a customer's allowed set.
 *  Mirrors the backend's _order_allowed_payment_methods: keep only known
 *  methods in a stable order, and treat null/empty as the card-only default. */
const resolvePaymentMethods = (raw: any): Array<'card' | 'ach'> => {
  const allowed = Array.isArray(raw) ? raw : [];
  const ordered = (['card', 'ach'] as const).filter((m) => allowed.includes(m));
  return ordered.length ? ordered : ['card'];
};

const mapDbOrder = (o: any): Order => {
  const today = new Date();
  const endDate = new Date(o.stops_at);
  const isLate = endDate < today && o.status !== 'archived';

  const lines: OrderLine[] = (o.lines || []).map((l: any) => ({
    id: l.id,
    itemType: l.item_type || 'Unknown',
    productName: l.title || 'Unknown Product',
    quantity: l.quantity || 0,
    priceEach: (l.price_each_cents || 0) / 100,
    totalPrice: (l.total_cents || 0) / 100,
  }));

  // item_count is the Booqable-sourced count on the mirror (sum of line
  // quantities), kept current by the order-totals refresh. The order_lines
  // mirror is not populated, so the lines-derived sum is only a fallback for
  // any row whose item_count hasn't been backfilled yet (#611).
  const itemCount = o.item_count ?? lines.reduce((sum, l) => sum + l.quantity, 0);
  const itemsSummary = lines.map(l => `${l.quantity}x ${l.productName}`).join(', ') || 'N/A';

  // Prefer the tax-inclusive total (what the customer is charged). Booqable's
  // grand_total_in_cents is pre-tax and frequently null on the mirror, which
  // showed orders at $0.00. Matches the customer-profile Orders tab.
  const grandTotalCents = o.grand_total_with_tax_in_cents ?? o.grand_total_in_cents ?? 0;
  const paidCents = o.amount_paid_in_cents || 0;
  // Derive the badge from the mirror's money columns (paid vs tax-inclusive
  // total) rather than trusting the persisted payment_status enum. That enum is
  // a third value multiple backend writers (charge refresh, totals refresh,
  // reconcile) must each remember to rewrite whenever money moves; when a
  // rewrite is missed the money is fresh but the enum sticks, and the badge goes
  // stale. Money is the same source those writers derive the enum from, so this
  // is never staler than the enum — it just removes the enum-vs-money drift.
  // Same four-state ladder + tax-inclusive basis the order detail page derives
  // from (derivePaymentStatus in OrderDetails), so list and header agree. A
  // zero-dollar (or not-yet-totaled) order has no balance to owe, so it carries
  // no status — null renders as a dash rather than a misleading "Paid".
  let paymentStatus: Order['paymentStatus'];
  if (grandTotalCents > 0) {
    paymentStatus =
      paidCents > grandTotalCents ? 'overpaid' :
      paidCents >= grandTotalCents ? 'paid' :
      paidCents > 0 ? 'partially_paid' :
      'payment_due';
  } else {
    paymentStatus = null;
  }

  return {
    id: o.id,
    booqable_id: o.booqable_id || null,
    orderNumber: String(o.number || 'N/A'),
    customerName: o.customer?.name || 'Unknown',
    customerId: o.customer?.id || null,
    customerEmail: o.customer?.email || '',
    startDate: o.starts_at || '',
    endDate: o.stops_at || '',
    items: itemsSummary,
    orderLines: lines,
    itemCount,
    location: o.delivery_address || 'N/A',
    status: isLate ? 'late' : 'upcoming',
    booqableStatus: (o.status || 'draft') as Order['booqableStatus'],
    price: grandTotalCents / 100,
    paidAmount: paidCents / 100,
    paymentStatus,
    createdAt: o.created_at || '',
    lastModified: o.last_edited_at || '',
    tagList: o.tag_list || [],
    debrisDescription: o.debris_description ?? null,
    prohibitedItemsAcknowledged: o.prohibited_items_acknowledged ?? null,
    waiveTechFee: !!o.waive_tech_fee,
    source: o.source ?? null,
    createdByName: o.created_by_name ?? null,
    project: o.project
      ? {
          id: o.project.id,
          name: o.project.name ?? null,
          projectNumber: o.project.project_number ?? null,
        }
      : null,
    paymentMethods: resolvePaymentMethods(o.customer?.allowed_payment_methods),
    invoiceSent: !!o.invoice_sent,
  };
};

// ── Dashboard helpers ──────────────────────────────────────────────────────

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

export const isToday = (dateStr: string): boolean => {
  if (!dateStr) return false;
  const d = startOfDay(new Date(dateStr));
  const t = startOfDay(new Date());
  return d.getTime() === t.getTime();
};

export const isTomorrow = (dateStr: string): boolean => {
  if (!dateStr) return false;
  const d = startOfDay(new Date(dateStr));
  const t = startOfDay(new Date());
  t.setDate(t.getDate() + 1);
  return d.getTime() === t.getTime();
};

export const isWithinHours = (dateStr: string, hours: number): boolean => {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  const now = new Date();
  return d >= now && d <= new Date(now.getTime() + hours * 3_600_000);
};

export const isPastDue = (dateStr: string): boolean => {
  if (!dateStr) return false;
  return new Date(dateStr) < new Date();
};

export const daysBetween = (dateStr: string): number => {
  const diff = new Date().getTime() - new Date(dateStr).getTime();
  return Math.floor(diff / 86_400_000);
};

/** Extract bin size from order lines, e.g. "20 yd" or first line name */
export const getBinSize = (order: Order): string => {
  for (const line of order.orderLines) {
    const m = line.productName.match(/(\d+)\s*yd/i);
    if (m) return `${m[1]}yd`;
  }
  return order.orderLines[0]?.productName?.substring(0, 12) || 'N/A';
};

/**
 * Format a delivery/pickup datetime (Booqable starts_at/stops_at — a real
 * timestamp) as a clock time like "8:00 AM", rendered in the business timezone
 * so every staff member reads the same wall-clock regardless of their location.
 */
export const fmtTime = (dateStr: string): string => {
  if (!dateStr) return 'TBD';
  return formatBusinessTime(dateStr) || 'TBD';
};

// Conditional-GET + delta state for the /orders feed. The Orders board polls
// on an interval; the last ETag goes back as If-None-Match so the server can
// answer 304 when nothing changed (we return the cached rows — no flash, no
// re-render churn, no body on the wire). When something DID change, we send the
// last `max_updated` cursor as ?since so the server ships only the changed rows
// (plus the live id set) instead of the whole window — see get_db_orders. We
// merge that delta into the cache here. The cross-origin read of ETag relies on
// Access-Control-Expose-Headers (set on the backend); same-origin /api proxy
// exposes it natively.
let _ordersEtag: string | null = null;
let _ordersCache: Order[] = [];
let _ordersSince: string | null = null;

export const fetchOrders = async (): Promise<Order[]> => {
  const qs = _ordersSince ? `?since=${encodeURIComponent(_ordersSince)}` : '';
  const response = await apiFetch(`/orders${qs}`, {
    headers: _ordersEtag ? { 'If-None-Match': _ordersEtag } : undefined,
  });
  if (response.status === 304) return _ordersCache;
  if (!response.ok) throw new Error(`Failed to fetch orders: ${response.statusText}`);
  const etag = response.headers.get('ETag');
  if (etag) _ordersEtag = etag;
  const data = await response.json();
  const rows: Order[] = (data.data || []).map(mapDbOrder);

  if (data.partial) {
    // Delta: upsert changed rows by id, then evict any cached order no longer
    // in the server's current window (current_ids), and re-sort to mirror the
    // server's created_at-desc ordering a full fetch would return.
    const byId = new Map(_ordersCache.map((o) => [o.id, o]));
    for (const row of rows) byId.set(row.id, row);
    if (Array.isArray(data.current_ids)) {
      const live = new Set<string>(data.current_ids);
      for (const id of Array.from(byId.keys())) {
        if (!live.has(id)) byId.delete(id);
      }
    }
    _ordersCache = Array.from(byId.values()).sort((a, b) =>
      (b.createdAt || '').localeCompare(a.createdAt || ''),
    );
  } else {
    _ordersCache = rows;
  }
  if (data.max_updated) _ordersSince = data.max_updated;
  return _ordersCache;
};

/**
 * Raw GET /orders/:id — the local Supabase mirror row in its full `{ data }`
 * envelope (NOT mapped to the Order type). This is the single source the
 * OrderDetails load effect, the right-sidebar waive-fee gate, and the
 * customer-notes placement gate all read, shared under
 * queryKeys.orders.localRow(id) so the row is fetched once per open instead of
 * three times. The full row is a superset of each consumer's slice (placement
 * keys, waive_tech_fee, lines, sections, number, customer, dates, …).
 */
export const fetchOrderLocalRow = async (
  orderId: string,
): Promise<{ data?: any }> => {
  const res = await apiFetch(`/orders/${encodeURIComponent(orderId)}`);
  if (!res.ok) throw new Error(`Failed to fetch order: ${res.status}`);
  return res.json();
};

export const fetchConceptOrders = async (): Promise<Order[]> => {
  const response = await apiFetch('/orders/concept');
  if (!response.ok) throw new Error(`Failed to fetch concept orders: ${response.statusText}`);
  const data = await response.json();
  return (data.data || []).map(mapDbOrder);
};
